<template>
  <view-container :loading="loading">
    <div class="flex justify-between flex-wrap gap-2 mb-6">
      <h3 class="font-bold">Hodnotenia</h3>
    </div>

    <template v-if="!reward_system">
      <p>
        Nemáš nastavený žiadny systém hodnotenia.
      </p>
    </template>

    <template v-if="reward_system">

      <div class="stats bg-neutral stats-vertical lg:stats-horizontal shadow mb-6 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="stat">
          <div class="stat-title">Level</div>
          <div class="stat-value">{{ stats.level }}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Celkové body</div>
          <div class="stat-value">{{ stats.reward_points_total }}</div>
          <div class="stat-desc">Mesačný priemer: {{ stats.average_points }}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Počet bodov na level up</div>
          <div class="stat-value">{{ stats.pointsToLevelUp }}</div>
          <div class="stat-desc">Počet mesiacov do daľšieho levelu: {{ stats.monthsToLevelUp }}</div>
        </div>
      </div>

      <div class="flex justify-end mb-2">
        <button class="btn btn-sm btn-ghost gap-1" @click="showPrices = !showPrices">
          <svg v-if="showPrices" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
            <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
          </svg>
          {{ showPrices ? 'Skryť výplatu' : 'Zobraziť výplatu' }}
        </button>
      </div>

      <div class="table">
        <div class="row-header" :style="{ gridTemplateColumns: gridColumns }">
          <span>Mesiac</span>
          <span>Level</span>
          <span>Dochádzka</span>
          <span>Body (celkom)</span>
          <span>Body do kar. rastu</span>
          <template v-if="showPrices">
            <span>Fix</span>
            <span>Variabilná</span>
            <span v-if="hasOvertime">Nadčas</span>
            <span>Celkom</span>
          </template>
        </div>
        <div class="row" :style="{ gridTemplateColumns: gridColumns }" v-for="rating in ratings" :key="rating.id" @click="selectRating(rating)">
          <span data-title="Mesiac">{{ rating.monthName }}<br><b>{{ rating.month }}. {{ rating.year }}</b></span>
          <span data-title="Level">
            <span class="badge badge-sm badge-primary">LEVEL {{ rating.level.name }}</span>
            <span class="badge badge-sm badge-warning" v-if="rating.level_changed">LEVEL UP</span>
            <span class="badge badge-sm badge-error" v-if="rating.is_frozen">FROZEN</span>
          </span>
          <span data-title="Dochádzka">{{ rating.hours_without_overtime }} h ({{ rating.work_percentage }} %)</span>
          <span data-title="Body (celkom)">{{ rating.points_sum }} / 5</span>
          <span data-title="Body do kar. rastu">{{ rating.final_points }} / 5</span>
          <template v-if="showPrices">
            <span data-title="Fix">{{ formatPrice(rating.work_price_fix) }}</span>
            <span data-title="Variabilná">{{ formatPrice(rating.work_price_variable) }}</span>
            <span v-if="hasOvertime" data-title="Nadčas">{{ formatPrice(rating.work_price_overtime) }}</span>
            <span data-title="Celkom" class="font-bold">{{ formatPrice(rating.work_price_all) }}</span>
          </template>
        </div>
      </div>

      <modal ref="modal" :middle="false">
        <template v-if="selectedRating">
          <h3 class="font-bold text-lg">Hodnotenie za {{ selectedRating.month }}. {{ selectedRating.year }}</h3>

          <div class="my-2 p-5 bg-base-200 rounded-md point-card" v-for="cat in pointCategories" :key="cat.key">
            <div class="flex justify-between items-center">
              <div class="flex gap-2 items-center">
                <span class="badge badge-sm badge-primary">{{ cat.weight }} %</span>
                <b>{{ cat.name }}</b>
                <small v-if="cat.description" class="text-neutral-500">{{ cat.description }}</small>
              </div>
              <span class="badge badge-secondary">{{ cat.point }}</span>
            </div>
            <div class="flex justify-end text-neutral-400 text-sm mt-1">
              <span>{{ formatPrice(cat.money) }} / {{ formatPrice(cat.maxMoney) }}</span>
            </div>
            <p v-if="cat.note" class="whitespace-pre-wrap text-neutral-400 text-sm mt-1">
              {{ cat.note }}
            </p>
          </div>

          <div class="my-2 p-5 bg-primary-content/10 rounded-md">
            <div class="flex justify-between">
              <b>Celkové body</b>
              <span class="badge badge-secondary">{{ selectedRating.points_sum }} / 5</span>
            </div>
          </div>

          <div class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>Bonusové body</b>
              <span class="badge badge-secondary">{{ selectedRating.bonus_points }}</span>
            </div>
            <p class="whitespace-pre-wrap text-neutral-400 text-sm">
              {{ selectedRating.note_bonus }}
            </p>
          </div>

          <div class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>Poznámka</b>
            </div>
            <p class="whitespace-pre-wrap text-neutral-400 text-sm">
              {{ selectedRating.note }}
            </p>
          </div>

          <div class="my-2 p-5 bg-primary-content/10 rounded-md">
            <div class="flex justify-between">
              <b>Body do kar. rastu</b>
              <span class="badge badge-secondary">{{ selectedRating.final_points }} / 5</span>
            </div>
          </div>

          <div class="divider">Dochádzka</div>

          <div class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>Odpracované hodiny</b>
              <span>{{ selectedRating.hours_without_overtime }} h</span>
            </div>
          </div>

          <div v-if="selectedRating.overtime_total > 0" class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>Nadčas</b>
              <span>{{ selectedRating.overtime_total }} h</span>
            </div>
          </div>

          <div v-if="selectedRating.vacation_total > 0" class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>DO</b>
              <span>{{ selectedRating.vacation_total }} h</span>
            </div>
          </div>

          <div v-if="selectedRating.ordered_vacation_total > 0" class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>Nariadená dovolenka</b>
              <span>{{ selectedRating.ordered_vacation_total }} h</span>
            </div>
          </div>

          <div class="my-2 p-5 bg-primary-content/10 rounded-md">
            <div class="flex justify-between">
              <b>Celkom</b>
              <span>{{ selectedRating.hours_total }} h ({{ selectedRating.work_percentage }} %)</span>
            </div>
          </div>

          <div class="divider">Výplata</div>

          <div class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>Fix</b>
              <span>{{ formatPrice(selectedRating.work_price_fix) }}</span>
            </div>
          </div>

          <div class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>Variabilná</b>
              <span>{{ formatPrice(selectedRating.work_price_variable) }} / {{ formatPrice(selectedRating.max_variable_price) }}</span>
            </div>
          </div>

          <div v-if="selectedRating.work_price_overtime > 0" class="my-2 p-5 bg-base-200 rounded-md">
            <div class="flex justify-between">
              <b>Nadčas</b>
              <span>{{ formatPrice(selectedRating.work_price_overtime) }}</span>
            </div>
          </div>

          <div class="my-2 p-5 bg-primary rounded-md">
            <div class="flex justify-between">
              <b>Celkom <span v-if="employee_type_id === 1" class="font-normal text-sm opacity-75">(v čistom)</span></b>
              <span class="font-bold text-lg">{{ formatPrice(selectedRating.work_price_all) }}</span>
            </div>
          </div>

          <div v-if="employee_type_id === 1" class="my-2 p-3 bg-warning/10 rounded-md text-sm text-warning">
            Pre TPP sa finálna výplata môže líšiť od zobrazenej sumy.
          </div>

        </template>
      </modal>

    </template>

  </view-container>
</template>

<script>
import { request } from '@/api/request'

export default {
  name: 'Rating',

  data: () => ({
    loading: 0,
    selectedRating: null,
    showPrices: false,
    ratings: [],
    reward_system: {},
    stats: {},
    employee_type_id: null
  }),
  computed: {
    hasOvertime() {
      return this.ratings.some(r => r.work_price_overtime > 0)
    },
    pointCategories() {
      if (!this.selectedRating) return []
      const r = this.selectedRating
      const rs = this.reward_system
      const maxVar = r.max_variable_price || 0
      return [
        { key: 'a', name: 'Dochádzka', point: r.point_a, money: r.money_a, weight: rs.weight_a, description: r.description_a, note: r.note_a },
        { key: 'b', name: rs.name_b, point: r.point_b, money: r.money_b, weight: rs.weight_b, description: r.description_b, note: r.note_b },
        { key: 'c', name: rs.name_c, point: r.point_c, money: r.money_c, weight: rs.weight_c, description: r.description_c, note: r.note_c },
        { key: 'd', name: rs.name_d, point: r.point_d, money: r.money_d, weight: rs.weight_d, description: r.description_d, note: r.note_d },
        { key: 'e', name: rs.name_e, point: r.point_e, money: r.money_e, weight: rs.weight_e, description: r.description_e, note: r.note_e },
      ].map(c => ({ ...c, maxMoney: maxVar * (c.weight / 100) }))
    },
    gridColumns() {
      if (!this.showPrices) {
        return '0.7fr 0.5fr 0.6fr 0.5fr 0.5fr'
      }
      return this.hasOvertime
        ? '0.6fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr'
        : '0.6fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr'
    }
  },
  methods: {
    async loadRatings() {
      const data = await request('getEmployeeRatings')
      if (data && data.success) {
        this.ratings = data.ratings
        this.reward_system = data.reward_system
        this.stats = data.stats
        this.employee_type_id = data.employee_type_id
      }
    },
    selectRating(rating) {
      this.selectedRating = rating
      this.$refs.modal.open()
    },
    formatPrice(value) {
      return value != null ? value.toFixed(2) + ' €' : '—'
    }
  },
  async created() {
    this.loading = 1
    await this.loadRatings()
    this.loading = 0
  }
}
</script>

<style lang="scss" scoped>
.table {
  width: 100%;
  color: #f5f5f5;

  .row-header {
    @apply bg-neutral;
    border-radius: 6px;
    font-size: 11px;
    display: grid;
    align-items: center;
    padding: 8px 16px;

    @media only screen and (max-width: 650px) {
      display: none;
    }

    span {
      display: block;
      padding: 4px;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:not(:last-child) {
        border-right: 1px solid #444;
      }
    }
  }

  .row {
    display: grid;
    align-items: center;
    margin: 12px 0px;
    min-height: 60px;
    padding: 8px 16px;
    @apply bg-neutral rounded-md;

    @media only screen and (max-width: 650px) {
      grid-template-columns: 1fr;
    }

    &:nth-child(odd) {
      @apply bg-opacity-40;
    }

    &:hover {
      @apply bg-primary;
      cursor: pointer;
    }

    & > span {
      display: block;
      padding: 8px;
      text-align: center;

      &.date {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        @media only screen and (max-width: 650px) {
          display: block;
          align-items: flex-start;
        }
      }

      &:not(:last-child) {
        border-right: 1px solid #444;
      }

      @media only screen and (max-width: 650px) {
        text-align: left;
        &:not(:last-child) {
          border-right: 0px solid #444;
        }
        &:before {
          content: attr(data-title);
          font-size: 11px;
          color: #999;
          display: block;
          margin-bottom: 6px;
        }
      }
    }
  }
}
</style>
